import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
    accountingOversightResponse,
    oversightActionItem,
    oversightCurrencyMode,
    oversightMetric
} from '../../../models/entities/businessPartner/accountingOversight';
import { businessPartner } from '../../../models/entities/businessPartner/businessPartner';
import { filterFieldsType } from '../../../state/ducks/allShipmentsPage/allShipmentsPage';
import { localSDK as client } from '../../../sdk';
import { RootState } from '../../../state/store/store';
import { PagesRoutes } from '../../../routing/PagesRoutes';
import Loader from '../../shared/SmallComponents/Loader';
import { DropDown } from '../../shared/inputs/base';
import { formatMoney } from '../businessPartners/singleBusinessPartner/sections/Accounting/format';
import OversightCharts from './OversightCharts';
import CargoOwnerPnlSection from './CargoOwnerPnlSection';
import {
    buildOversightShipmentsFilter,
    OversightFilterPreset
} from './oversightShipmentsFilter';
import {
    Page,
    PageHeader,
    PageTitle,
    FilterBar,
    FilterField,
    KpiRow,
    KpiCard,
    KpiLabel,
    KpiValue,
    KpiSub,
    MainGrid,
    Section,
    SectionHeader,
    SectionTitle,
    TableCard,
    Table,
    Th,
    Td,
    Tr,
    EmptyTd,
    ShipmentLink,
    EmptyHint,
    ActionList,
    ActionCard,
    ActionTop,
    ActionTitle,
    PriorityBadge,
    ActionStep,
    ActionMeta,
    ActionFooter,
    ActionImpact,
    ActionButtons,
    SecondaryBtn,
    PrimaryBtn,
    RecoverButton,
    DrawerOverlay,
    Drawer,
    DrawerHeader,
    DrawerBody,
    LeakLinks,
    LeakChip,
    NativeCurrencyPanel
} from './AccountingOversightStyles';

type Props = {
    businessPartners: businessPartner[];
    setShipmentFilters: (filter: filterFieldsType) => void;
    fetchShipmentsByFilter: (filter: filterFieldsType) => void;
    fetchBusinessPartners: () => void;
};

const ALL_CARGO_OWNERS = '__ALL__';

const CURRENCY_OPTIONS = [
    { value: 'base', text: 'Base currency' },
    { value: 'native', text: 'Native currencies' }
];

const PRIORITY_LABEL = { high: 'Urgent', medium: 'Soon', low: 'Low' } as const;

const AccountingOversightPage = ({
    businessPartners,
    setShipmentFilters,
    fetchShipmentsByFilter,
    fetchBusinessPartners
}: Props) => {
    const navigate = useNavigate();
    const cargoOwners = useMemo(
        () =>
            businessPartners
                .filter((bp) => bp.type === 'CARGO_OWNER' && bp.isActive !== false)
                .slice()
                .sort((a, b) =>
                    (a.name || a.BusinessPartnerNumber || a.id).localeCompare(
                        b.name || b.BusinessPartnerNumber || b.id,
                        undefined,
                        { sensitivity: 'base' }
                    )
                ),
        [businessPartners]
    );

    const [cargoOwnerId, setCargoOwnerId] = useState<string>(ALL_CARGO_OWNERS);
    const [currencyMode, setCurrencyMode] = useState<oversightCurrencyMode>('base');
    const [data, setData] = useState<accountingOversightResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [recoverOpen, setRecoverOpen] = useState(false);

    const isAllView = cargoOwnerId === ALL_CARGO_OWNERS;

    useEffect(() => {
        fetchBusinessPartners();
    }, [fetchBusinessPartners]);

    const load = useCallback(async () => {
        if (!cargoOwnerId) return;
        setLoading(true);
        setError(null);
        try {
            const res = await client.businessPartners().accounting().fetchOversight({
                cargoOwnerId: isAllView ? ALL_CARGO_OWNERS : cargoOwnerId,
                currencyMode,
                displayCurrency: 'USD'
            });
            setData(res);
        } catch (e) {
            const networkFailed = e instanceof TypeError || (e instanceof Error && /failed to fetch|networkerror|load failed/i.test(e.message));
            const httpMessage =
                e && typeof e === 'object' && 'message' in e && typeof (e as { message: unknown }).message === 'string'
                    ? (e as { message: string }).message
                    : null;
            setError(
                networkFailed
                    ? isAllView
                        ? 'Company-wide oversight timed out. Select a single cargo owner, or retry in a moment.'
                        : 'Failed to reach accounting oversight API. Please retry.'
                    : e instanceof Error
                      ? e.message
                      : httpMessage || 'Failed to load accounting oversight'
            );
            setData(null);
        } finally {
            setLoading(false);
        }
    }, [cargoOwnerId, currencyMode, isAllView]);

    useEffect(() => {
        load();
    }, [load]);

    const currency = data?.displayCurrency ?? 'USD';
    const fmt = (amount: number, cur?: string) => formatMoney(amount, cur ?? currency);

    const cargoOwnerOptions = useMemo(
        () => [
            { value: ALL_CARGO_OWNERS, text: 'All cargo owners' },
            ...cargoOwners.map((bp) => ({
                value: bp.id,
                text: bp.name || bp.BusinessPartnerNumber || bp.id
            }))
        ],
        [cargoOwners]
    );

    const openInShipmentsGrid = (preset: OversightFilterPreset) => {
        if (!cargoOwnerId) return;
        const filter = buildOversightShipmentsFilter(preset, isAllView ? null : cargoOwnerId);
        setShipmentFilters(filter);
        fetchShipmentsByFilter(filter);
        navigate(PagesRoutes.Shipments);
    };

    const goToShipment = (shipmentId: string) => {
        navigate(`${PagesRoutes.Shipments}/${shipmentId}`);
    };

    const scopeLabel = isAllView
        ? 'All cargo owners'
        : data?.cargoOwnerName ||
          cargoOwners.find((bp) => bp.id === cargoOwnerId)?.name ||
          'Cargo owner';

    const renderActionItem = (item: oversightActionItem) => (
        <ActionCard key={item.id} $priority={item.priority}>
            <ActionTop>
                <ActionTitle>{item.action}</ActionTitle>
                <PriorityBadge $priority={item.priority}>{PRIORITY_LABEL[item.priority]}</PriorityBadge>
            </ActionTop>
            <ActionStep>{item.suggestedStep}</ActionStep>
            <ActionMeta>{item.event}</ActionMeta>
            <ActionFooter>
                <ActionImpact>{formatMoney(item.impact, item.impactCurrency)}</ActionImpact>
                <ActionButtons>
                    {item.shipmentId && (
                        <SecondaryBtn type="button" onClick={() => goToShipment(item.shipmentId!)}>
                            Open file
                        </SecondaryBtn>
                    )}
                    <PrimaryBtn
                        type="button"
                        onClick={() =>
                            openInShipmentsGrid((item.filterPreset as OversightFilterPreset) || 'cargoOwnerAll')
                        }
                    >
                        View in grid
                    </PrimaryBtn>
                </ActionButtons>
            </ActionFooter>
        </ActionCard>
    );

    if (!cargoOwners.length) {
        return (
            <Page>
                <EmptyHint>No cargo owners found. Add a cargo owner business partner to use this dashboard.</EmptyHint>
            </Page>
        );
    }

    return (
        <Page>
            <PageHeader>
                <PageTitle>Accounting Oversight</PageTitle>
            </PageHeader>

            <FilterBar>
                <FilterField>
                    <DropDown
                        value={cargoOwnerId}
                        onChange={(v) => setCargoOwnerId(String(v))}
                        options={cargoOwnerOptions}
                        placeHolder="Cargo owner"
                    />
                </FilterField>
                <FilterField>
                    <DropDown
                        value={currencyMode}
                        onChange={(v) => setCurrencyMode(v as oversightCurrencyMode)}
                        options={CURRENCY_OPTIONS}
                        placeHolder="Currency"
                    />
                </FilterField>
            </FilterBar>

            {loading && <Loader />}
            {error && <EmptyHint>{error}</EmptyHint>}

            {data && !loading && (
                <>
                    <KpiRow>
                        <KpiCard
                            type="button"
                            $variant="danger"
                            disabled={data.revenueLeakage.total <= 0}
                            onClick={() => openInShipmentsGrid('notInvoiced')}
                        >
                            <KpiLabel>Revenue at risk</KpiLabel>
                            <KpiValue $tone="danger">{fmt(data.revenueLeakage.total)}</KpiValue>
                            <KpiSub>
                                {data.revenueLeakage.notInvoiced.count} not invoiced · {scopeLabel}
                            </KpiSub>
                        </KpiCard>
                        <KpiCard
                            type="button"
                            disabled={data.collection.currentTotal <= 0}
                            onClick={() => openInShipmentsGrid('overdue')}
                        >
                            <KpiLabel>Open to collect</KpiLabel>
                            <KpiValue $tone="info">{fmt(data.collection.currentTotal)}</KpiValue>
                            <KpiSub>{data.moneyLeakRadar.overdueCollections.count} overdue files</KpiSub>
                        </KpiCard>
                        <KpiCard
                            type="button"
                            disabled={data.billingControl.readyForBilling <= 0}
                            onClick={() => openInShipmentsGrid('readyToIssue')}
                        >
                            <KpiLabel>Ready to bill</KpiLabel>
                            <KpiValue $tone="success">{data.billingControl.readyForBilling}</KpiValue>
                            <KpiSub>Files at Ready to issue</KpiSub>
                        </KpiCard>
                        <KpiCard
                            type="button"
                            disabled={data.moneyAtRisk.totalCount <= 0}
                            onClick={() => setRecoverOpen(true)}
                        >
                            <KpiLabel>Money at risk</KpiLabel>
                            <KpiValue $tone="warn">{fmt(data.moneyAtRisk.totalAmount)}</KpiValue>
                            <KpiSub>{data.moneyAtRisk.totalCount} issues · Recover</KpiSub>
                        </KpiCard>
                    </KpiRow>

                    <MainGrid>
                        <div>
                            <OversightCharts data={data} currency={currency} />
                            {currencyMode === 'native' && data.nativeCurrencies.length > 0 && (
                                <NativeCurrencyPanel>
                                    {data.nativeCurrencies.map((cur) => {
                                        const n = data.amountsByCurrency[cur];
                                        if (!n) return null;
                                        return (
                                            <div key={cur}>
                                                <strong>{cur}</strong> — Collection{' '}
                                                {formatMoney(n.expectedCollection, cur)} · Payments{' '}
                                                {formatMoney(n.expectedPayments, cur)} · Balance{' '}
                                                {formatMoney(n.forecastBalance, cur)}
                                            </div>
                                        );
                                    })}
                                </NativeCurrencyPanel>
                            )}
                        </div>

                        <Section>
                            <SectionHeader>
                                <SectionTitle>What needs attention</SectionTitle>
                            </SectionHeader>
                            <ActionList>
                                {data.actionItems.length === 0 && (
                                    <EmptyHint style={{ padding: '12px 0' }}>No urgent actions right now.</EmptyHint>
                                )}
                                {data.actionItems.slice(0, 8).map(renderActionItem)}
                            </ActionList>
                        </Section>
                    </MainGrid>

                    {isAllView && <CargoOwnerPnlSection displayCurrency={currency} />}

                    <Section>
                        <SectionHeader>
                            <SectionTitle>Top overdue shipments</SectionTitle>
                            <SecondaryBtn type="button" onClick={() => openInShipmentsGrid('overdue')}>
                                View all in grid
                            </SecondaryBtn>
                        </SectionHeader>
                        <TableCard>
                            <Table>
                                <thead>
                                    <tr>
                                        <Th>Shipment</Th>
                                        <Th>Sales person</Th>
                                        <Th $right>Aging</Th>
                                        <Th $right>Overdue</Th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.collection.topOverdue.length === 0 && (
                                        <tr>
                                            <EmptyTd colSpan={4}>
                                                {isAllView
                                                    ? 'No overdue shipments across cargo owners.'
                                                    : 'No overdue shipments for this cargo owner.'}
                                            </EmptyTd>
                                        </tr>
                                    )}
                                    {data.collection.topOverdue.map((row) => (
                                        <Tr key={row.shipmentId}>
                                            <Td>
                                                <ShipmentLink
                                                    type="button"
                                                    onClick={() => goToShipment(row.shipmentId)}
                                                >
                                                    {row.shipmentNumber}
                                                </ShipmentLink>
                                            </Td>
                                            <Td>{row.salesPerson || '—'}</Td>
                                            <Td $right>{row.agingDays}d</Td>
                                            <Td $right>{formatMoney(row.overdueAmount, row.currency)}</Td>
                                        </Tr>
                                    ))}
                                </tbody>
                            </Table>
                        </TableCard>
                    </Section>

                    <Section>
                        <SectionHeader>
                            <SectionTitle>Leakage breakdown</SectionTitle>
                            <RecoverButton type="button" onClick={() => setRecoverOpen(true)}>
                                Recover money
                            </RecoverButton>
                        </SectionHeader>
                        <LeakLinks>
                            <LeakChip type="button" onClick={() => openInShipmentsGrid('deliveredNotInvoiced')}>
                                Delivered w/o invoice{' '}
                                <strong>{data.revenueLeakage.releasedWithoutInvoice.count}</strong>
                            </LeakChip>
                            <LeakChip type="button" onClick={() => openInShipmentsGrid('readyToIssue')}>
                                Ready to issue <strong>{data.revenueLeakage.readyToIssue.count}</strong>
                            </LeakChip>
                            <LeakChip type="button" onClick={() => openInShipmentsGrid('notInvoiced')}>
                                Not invoiced <strong>{fmt(data.moneyLeakRadar.notInvoiced.amount)}</strong>
                            </LeakChip>
                            <LeakChip type="button" onClick={() => openInShipmentsGrid('waitingVendorInvoice')}>
                                Vendor invoice <strong>{data.billingControl.waitingVendorInvoice}</strong>
                            </LeakChip>
                            <LeakChip type="button" onClick={() => openInShipmentsGrid('negativeProfit')}>
                                Negative profit <strong>{data.revenueLeakage.negativeProfit.count}</strong>
                            </LeakChip>
                        </LeakLinks>
                        {data.billingControl.avgDaysReadyToInvoiceSent != null && (
                            <KpiSub style={{ marginTop: 8 }}>
                                Avg. {data.billingControl.avgDaysReadyToInvoiceSent} days from file open to invoice
                                sent
                            </KpiSub>
                        )}
                    </Section>
                </>
            )}

            {recoverOpen && data && (
                <>
                    <DrawerOverlay onClick={() => setRecoverOpen(false)} />
                    <Drawer>
                        <DrawerHeader>
                            <h3>Recover money · {fmt(data.moneyAtRisk.totalAmount)}</h3>
                            <button type="button" onClick={() => setRecoverOpen(false)}>
                                ×
                            </button>
                        </DrawerHeader>
                        <DrawerBody>
                            <RecoverRow
                                label="Delivered without invoice"
                                metric={data.moneyAtRisk.deliveredWithoutInvoice}
                                preset="deliveredWithoutInvoice"
                                onGrid={openInShipmentsGrid}
                                onShipment={goToShipment}
                                fmt={fmt}
                            />
                            <RecoverRow
                                label="Invoices aging 120+ days"
                                metric={data.moneyAtRisk.invoicesAgingAbove120}
                                preset="overdue"
                                onGrid={openInShipmentsGrid}
                                onShipment={goToShipment}
                                fmt={fmt}
                            />
                            <RecoverRow
                                label="Delivered 10+ days, no vendor invoice"
                                metric={data.moneyAtRisk.deliveredNoVendorInvoice10d}
                                preset="deliveredNotInvoiced"
                                onGrid={openInShipmentsGrid}
                                onShipment={goToShipment}
                                fmt={fmt}
                            />
                            <RecoverRow
                                label="FCL not released · week+ since ATA"
                                metric={data.moneyAtRisk.fclNotReleasedWeekSinceAta}
                                preset="cargoOwnerAll"
                                onGrid={openInShipmentsGrid}
                                onShipment={goToShipment}
                                fmt={fmt}
                            />
                            <RecoverRow
                                label="Buying rate below selling rate"
                                metric={data.moneyAtRisk.buyingRateBelowSelling}
                                preset="cargoOwnerAll"
                                onGrid={openInShipmentsGrid}
                                onShipment={goToShipment}
                                fmt={fmt}
                            />
                            {data.moneyAtRisk.customersAbove90Obligo.count > 0 && (
                                <KpiSub style={{ marginTop: 12 }}>
                                    Obligo above 90%: {fmt(data.moneyAtRisk.customersAbove90Obligo.amount)}
                                </KpiSub>
                            )}
                        </DrawerBody>
                    </Drawer>
                </>
            )}
        </Page>
    );
};

type RecoverRowProps = {
    label: string;
    metric: oversightMetric;
    preset: OversightFilterPreset;
    fmt: (n: number) => string;
    onGrid: (preset: OversightFilterPreset) => void;
    onShipment: (id: string) => void;
};

const RecoverRow = ({ label, metric, preset, fmt, onGrid, onShipment }: RecoverRowProps) => (
    <div style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
            <strong style={{ fontSize: 13 }}>
                {label}: {metric.count} ({fmt(metric.amount)})
            </strong>
            {metric.count > 0 && (
                <SecondaryBtn type="button" onClick={() => onGrid(preset)}>
                    Grid
                </SecondaryBtn>
            )}
        </div>
        {metric.shipments.slice(0, 4).map((s) => (
            <div key={s.shipmentId} style={{ marginTop: 4 }}>
                <ShipmentLink type="button" onClick={() => onShipment(s.shipmentId)}>
                    {s.shipmentNumber}
                </ShipmentLink>
            </div>
        ))}
    </div>
);

const mapStateToProps = (state: RootState) => ({
    businessPartners: state.businessPartners.parties ?? []
});

const mapDispatchToProps = (dispatch: {
    allShipmentsPage: {
        setShipmentFilters: (p: { filter: filterFieldsType }) => void;
        fetchShipmentsByFilter: (filter: filterFieldsType) => void;
    };
    businessPartners: { fetchBusinessPartners: () => void };
}) => ({
    setShipmentFilters: (filter: filterFieldsType) => dispatch.allShipmentsPage.setShipmentFilters({ filter }),
    fetchShipmentsByFilter: (filter: filterFieldsType) => dispatch.allShipmentsPage.fetchShipmentsByFilter(filter),
    fetchBusinessPartners: () => dispatch.businessPartners.fetchBusinessPartners()
});

export default connect(mapStateToProps, mapDispatchToProps as never)(AccountingOversightPage);
